import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { jsPDF } from "jspdf";
import { api, fmtMXN, WHATSAPP_NUMBER } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";

const BRAND = { primary: [224, 122, 95], ink: [41, 37, 36], muted: [120, 113, 108] };

const WELCOME_INTRO =
  "Agradecemos tu eleccion y estamos muy contentos de que pases un tiempo " +
  "sensacional en una playa preciosa hospedandote con nosotros, disfrutando la PURA VIDA.";

const ACCESS_PARAGRAPHS = [
  "El acceso a la propiedad privada es POR PLAYA.",
  "Desde el centro de Tuxpan a la casa son aprox. 30 minutos en auto. Se toma el camino que rodea la termoelectrica y el acceso final se realiza mediante un recorrido escenico por la playa de aprox. 10 a 15 minutos, lo que permite conservar el entorno natural y la tranquilidad que caracteriza a Barra de Galindo.",
  "El mejor horario de llegada es de 2:00 a 5:00 PM, cuando aun hay luz de dia. Por la noche la marea sube y eso podria complicar tu llegada.",
];

const HOUSE_RULES = [
  "Llegada (Check-in): 3:00 PM  -  Salida (Check-out): 12:00 PM.",
  "Por respeto al entorno y a los vecinos, no se permite musica fuerte despues de las 10:00 PM.",
  "Esta es una zona de playa virgen. Por favor llevate tu basura y cuida la fauna local (cangrejos, tortugas, aves).",
  "Mascotas: solo bajo previa autorizacion del anfitrion.",
  "Esta prohibido fumar dentro de la propiedad.",
  "Debido a las caracteristicas naturales de la zona, ocasionalmente pueden presentarse variaciones en internet o energia electrica. Estaremos atentos para apoyarte.",
];

const ARRIVAL_TIPS = [
  "Te sugerimos traer tus alimentos, bebidas y articulos personales para toda tu estancia; los comercios mas cercanos estan a cierta distancia.",
  "Carga combustible en Tuxpan antes de salir; no hay gasolineras cerca de la playa.",
  "Lleva efectivo para cualquier consumo con los vendedores locales.",
  "Trae repelente para mosquitos y usalo siempre antes de salir de la casa para disfrutar caminatas al amanecer, al ocaso o nocturnas (cangrejos, fogata).",
  "Te recomendamos llevar placas de Raid repelentes para conectar por las noches dentro de la casa.",
  "Senal celular limitada. Hay WiFi en la casa. Te sugerimos descargar mapas offline antes de salir.",
];

function buildBookingPdf(b) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const margin = 48;
  let y = 56;

  // Header band
  doc.setFillColor(...BRAND.primary);
  doc.rect(0, 0, W, 14, "F");

  // Brand
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.primary);
  doc.text("VILLAS PURA VIDA", margin, y);
  y += 28;

  doc.setFont("times", "italic");
  doc.setFontSize(28);
  doc.setTextColor(...BRAND.ink);
  doc.text("Confirmacion de Reserva", margin, y);
  y += 14;
  doc.setDrawColor(...BRAND.primary);
  doc.setLineWidth(1);
  doc.line(margin, y, margin + 60, y);
  y += 26;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.muted);
  doc.text("Gracias por reservar directo con nosotros.", margin, y);
  y += 22;

  // Booking details box
  doc.setDrawColor(230, 226, 220);
  doc.rect(margin, y, W - margin * 2, 150);
  let by = y + 24;
  const col1 = margin + 16;
  const col2 = margin + 200;

  const row = (label, value) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.muted);
    doc.text(label.toUpperCase(), col1, by);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...BRAND.ink);
    doc.text(String(value || "-"), col2, by);
    by += 22;
  };

  row("Reservacion", b.id ? b.id.slice(0, 8).toUpperCase() : "-");
  row("Propiedad", b.property_name || "-");
  row("Huesped", b.name || "-");
  row("Check-in", `${b.check_in}  (3:00 PM)`);
  row("Check-out", `${b.check_out}  (12:00 PM)`);
  row("Huespedes", `${b.guests || 1} persona(s)`);

  y += 170;

  // Total
  doc.setFillColor(250, 247, 243);
  doc.rect(margin, y, W - margin * 2, 44, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...BRAND.ink);
  doc.text("TOTAL PAGADO", margin + 16, y + 27);
  doc.setFontSize(16);
  doc.setTextColor(...BRAND.primary);
  doc.text(fmtMXN(b.total_web || 0), W - margin - 16, y + 27, { align: "right" });
  y += 70;

  // Section helper
  const section = (title) => {
    doc.setFont("times", "italic");
    doc.setFontSize(16);
    doc.setTextColor(...BRAND.ink);
    doc.text(title, margin, y);
    y += 6;
    doc.setDrawColor(...BRAND.primary);
    doc.line(margin, y, margin + 40, y);
    y += 18;
  };

  const paragraph = (text, italic = false) => {
    doc.setFont("helvetica", italic ? "italic" : "normal");
    doc.setFontSize(10.5);
    doc.setTextColor(...BRAND.ink);
    const lines = doc.splitTextToSize(text, W - margin * 2);
    lines.forEach((ln) => {
      if (y > 780) { doc.addPage(); y = 56; }
      doc.text(ln, margin, y);
      y += 14;
    });
    y += 8;
  };

  const bullets = (items) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.5);
    doc.setTextColor(...BRAND.ink);
    items.forEach((it) => {
      const lines = doc.splitTextToSize(`-  ${it}`, W - margin * 2 - 12);
      lines.forEach((ln) => {
        if (y > 780) {
          doc.addPage();
          y = 56;
        }
        doc.text(ln, margin + 6, y);
        y += 15;
      });
      y += 3;
    });
    y += 6;
  };

  // Bienvenida
  section("Bienvenida");
  paragraph(WELCOME_INTRO, true);

  // Como llegar
  if (y > 600) { doc.addPage(); y = 56; }
  section("Como llegar a la propiedad");
  ACCESS_PARAGRAPHS.forEach((p) => paragraph(p));

  // Antes de salir
  if (y > 600) { doc.addPage(); y = 56; }
  section("Recomendaciones antes de llegar");
  bullets(ARRIVAL_TIPS);

  // Reglas
  if (y > 600) { doc.addPage(); y = 56; }
  section("Reglas de la casa");
  bullets(HOUSE_RULES);

  if (y > 720) { doc.addPage(); y = 56; }
  section("Contacto del anfitrion");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...BRAND.ink);
  doc.text(`WhatsApp: +${WHATSAPP_NUMBER}`, margin, y);
  y += 18;
  doc.text("Email: reservas@puravidatuxpan.com", margin, y);
  y += 18;
  doc.setTextColor(...BRAND.muted);
  doc.setFontSize(9.5);
  doc.text("Si tienes cualquier duda en el camino, escribenos por WhatsApp.", margin, y);

  // Footer
  const PH = doc.internal.pageSize.getHeight();
  doc.setFillColor(...BRAND.primary);
  doc.rect(0, PH - 8, W, 8, "F");

  const filename = `Reservacion-${(b.id || "PuraVida").slice(0, 8)}.pdf`;
  doc.save(filename);
}

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { t } = useLang();
  const [state, setState] = useState({ status: "loading", data: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "failed", data: null });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", data });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "failed", data });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 8) {
          setState({ status: "pending", data });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setState({ status: "failed", data: null });
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const handleDownloadPdf = () => {
    if (state.data?.booking) buildBookingPdf(state.data.booking);
  };

  const handleWhatsapp = () => {
    const b = state.data?.booking;
    const msg = b
      ? `Hola, soy ${b.name}. Acabo de reservar ${b.property_name} del ${b.check_in} al ${b.check_out}. Me gustaria coordinar mi llegada.`
      : "Hola, acabo de reservar en Villas Pura Vida y me gustaria coordinar mi llegada.";
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div data-testid="success-page" className="min-h-screen flex items-center justify-center bg-[#FAFAF9] px-6">
      <div className="max-w-2xl w-full text-center py-24">
        {state.status === "loading" && (
          <p data-testid="status-loading" className="text-stone-500">{t("success.pending")}</p>
        )}
        {state.status === "paid" && state.data?.booking && (
          <>
            <div className="text-eyebrow text-[#E07A5F] mb-4" data-testid="success-eyebrow">Villas Pura Vida</div>
            <h1 className="font-serif-display text-5xl md:text-6xl mb-3 leading-none">
              {t("success.title")}
            </h1>
            <p className="font-serif-display italic text-xl text-stone-500 mb-6">
              {state.data.booking.property_name}
            </p>
            <p className="text-stone-600 text-lg mb-8">{t("success.sub")}</p>

            <div className="border border-stone-200 bg-white p-6 text-left mb-6">
              <div className="flex justify-between py-2 border-b border-stone-100">
                <span className="text-stone-500">Check-in</span>
                <span className="font-medium" data-testid="success-checkin">{state.data.booking.check_in}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-stone-100">
                <span className="text-stone-500">Check-out</span>
                <span className="font-medium" data-testid="success-checkout">{state.data.booking.check_out}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-stone-100">
                <span className="text-stone-500">{t("booking.guests")}</span>
                <span className="font-medium">{state.data.booking.guests}</span>
              </div>
              <div className="flex justify-between py-2 pt-3">
                <span className="text-stone-500">Total</span>
                <span className="font-medium" data-testid="success-total">{fmtMXN(state.data.booking.total_web)}</span>
              </div>
            </div>

            <div className="border border-[#E07A5F]/30 bg-[#FFF8F4] p-5 text-left mb-8">
              <p className="text-eyebrow text-[#E07A5F] mb-2">Antes de llegar</p>
              <p className="text-sm text-stone-700 leading-relaxed">
                El acceso a la propiedad es <b>POR PLAYA</b>: 30 min desde Tuxpan + 10–15 min escénicos por la playa.
                Mejor horario de llegada <b>2–5 PM</b> (por la noche sube la marea). Trae alimentos, efectivo, repelente
                de mosquitos y carga combustible antes. Descarga el PDF para ver todas las instrucciones.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <Button
                data-testid="download-pdf-btn"
                onClick={handleDownloadPdf}
                className="flex-1 bg-[#E07A5F] hover:bg-[#D3674B] text-white rounded-none px-6 py-6 text-sm uppercase tracking-[0.18em] font-semibold"
              >
                <i className="fa-solid fa-file-arrow-down mr-2" />
                Descargar reservación (PDF)
              </Button>
              <Button
                data-testid="whatsapp-host-btn"
                onClick={handleWhatsapp}
                variant="outline"
                className="flex-1 border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-white rounded-none px-6 py-6 text-sm uppercase tracking-[0.18em] font-semibold"
              >
                <i className="fa-brands fa-whatsapp mr-2" />
                Contactar al anfitrión
              </Button>
            </div>

            <Link to="/">
              <Button data-testid="success-home-btn" variant="ghost" className="text-stone-500 hover:text-stone-900 rounded-none">
                {t("success.home")}
              </Button>
            </Link>
          </>
        )}
        {state.status === "pending" && (
          <p className="text-stone-600" data-testid="status-pending">{t("success.pending")}</p>
        )}
        {state.status === "failed" && (
          <>
            <h1 className="font-serif-display text-4xl mb-4" data-testid="status-failed">{t("success.failed")}</h1>
            <Link to="/">
              <Button className="bg-[#E07A5F] hover:bg-[#D3674B] text-white rounded-none px-8 py-6">
                {t("success.retry")}
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
